import {useReturnFocus, type Focusable} from '@github-ui/use-return-focus'
import {Banner} from '@primer/react'
import type {RefObject} from 'react'
import type {UseMutationResult} from '@github-ui/react-query'
import {
  AuthSessionExpiredError,
  ServerUnavailableError,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {MarkGithubIcon} from '@primer/octicons-react'

interface FailureBannerProps {
  title: string
  error: string | Error
  onDismiss?: () => void
  /** Ref to an element to return focus to upon dismissal. */
  returnFocusRef: RefObject<Focusable | null>
  className?: string
  headingLevel?: 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
}

/**
 * Inline failure banner to report when user actions fail to complete. Often used directly with mutations, in which
 * case it's easier to use the `MutationFailureBanner`.
 */
export function FailureBanner({title, error, onDismiss, returnFocusRef, className, headingLevel}: FailureBannerProps) {
  const {containerRef: bannerRef} = useReturnFocus(returnFocusRef)

  const action =
    error instanceof ServerUnavailableError ? (
      <Banner.PrimaryAction as="a" href="https://www.githubstatus.com/" leadingVisual={MarkGithubIcon}>
        GitHub status
      </Banner.PrimaryAction>
    ) : error instanceof AuthSessionExpiredError ? (
      <Banner.PrimaryAction onClick={() => window.location.reload()}>Refresh page</Banner.PrimaryAction>
    ) : null

  return (
    <Banner
      variant="critical"
      onDismiss={onDismiss}
      role="alert"
      ref={bannerRef}
      className={className}
      primaryAction={action}
      actionsLayout="inline"
    >
      <Banner.Title as={headingLevel}>{title}</Banner.Title>
      <span>{typeof error === 'string' ? error : error.message}</span>
    </Banner>
  )
}

interface MutationFailureBannerProps extends Pick<
  FailureBannerProps,
  'headingLevel' | 'returnFocusRef' | 'className' | 'title'
> {
  // Just picking the fields we need makes the types much easier to deal with since we can avoid any generics
  mutation: Pick<UseMutationResult, 'error' | 'reset'>
}

/** Conditional failure banner which appears when the passed `mutation` is in an error state. */
export function MutationFailureBanner({mutation, ...forwardProps}: MutationFailureBannerProps) {
  if (!mutation.error) return

  return <FailureBanner error={mutation.error} onDismiss={() => mutation.reset()} {...forwardProps} />
}
